import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { formatDateKey } from "@/lib/time";

const STORAGE_KEY = "daily-groove:alerted-task-ids";

function loadAlerted(): Set<number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed);
  } catch {
    /* noop */
  }
  return new Set();
}

function saveAlerted(set: Set<number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* noop */
  }
}

/**
 * Polls today's tasks and fires browser notifications when a pending task with
 * alerts enabled becomes due. Each task instance is alerted at most once per
 * device (tracked via localStorage).
 */
export function useTaskAlerts() {
  const todayKey = formatDateKey(new Date());
  const tasksQuery = trpc.day.list.useQuery(
    { date: todayKey },
    { refetchInterval: 60_000 },
  );
  const alertedRef = useRef<Set<number>>(loadAlerted());

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Don't auto-prompt on every page; the user can opt in via the bell button.
    }
  }, []);

  useEffect(() => {
    const tasks = tasksQuery.data ?? [];
    if (tasks.length === 0) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const tick = () => {
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      let changed = false;
      for (const t of tasks) {
        if (!t.alertEnabled) continue;
        if (t.status !== "pending") continue;
        if (t.startMinutes === null) continue;
        if (t.startMinutes > nowMin) continue;
        // Don't alert for tasks more than 30 minutes in the past.
        if (nowMin - t.startMinutes > 30) continue;
        if (alertedRef.current.has(t.id)) continue;
        try {
          new Notification(t.title, {
            body: `${t.listName} · ${String(Math.floor(t.startMinutes / 60)).padStart(2, "0")}:${String(t.startMinutes % 60).padStart(2, "0")}${t.details ? ` — ${t.details}` : ""}`,
            tag: `daily-groove-task-${t.id}`,
          });
          alertedRef.current.add(t.id);
          changed = true;
        } catch {
          /* noop */
        }
      }
      if (changed) saveAlerted(alertedRef.current);
    };

    tick();
    const handle = window.setInterval(tick, 30_000);
    return () => window.clearInterval(handle);
  }, [tasksQuery.data]);
}

export async function requestNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  if (Notification.permission === "granted") return "granted";
  return Notification.requestPermission();
}

export function notificationPermission(): NotificationPermission | "unsupported" {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}
